import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Empty,
  List,
  Row,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import {
  DashboardHealthState,
  FreshnessEntry,
  HealthCheck,
  HealthSeverity,
  ProviderHealth,
} from "../../helpers/interfaces/DashboardInterfaces";
import "./dashboard.css";

const { Text } = Typography;

const SEVERITY_COLOR: Record<HealthSeverity, string> = {
  critical: "red",
  warning: "orange",
  ok: "green",
};

interface DashboardIndexProps extends DashboardHealthState {
  firstName?: string | null;
}

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString() : "—";

const formatAge = (age: number | null) => {
  if (age === null) return "no data";
  if (age === 0) return "today";
  return `${age} day${age === 1 ? "" : "s"} ago`;
};

const CheckItem = ({ check }: { check: HealthCheck }) => (
  <List.Item className="dash-check" data-testid={`dashboard-check-${check.key}`}>
    <div className="dash-check-row">
      <div className="dash-check-main">
        <div className="dash-check-heading">
          <Tag color={SEVERITY_COLOR[check.severity]}>
            {check.severity.toUpperCase()}
          </Tag>
          <Text strong>{check.title}</Text>
        </div>
        <Text type="secondary">{check.description}</Text>
      </div>
      <div className="dash-check-side">
        <Text strong>
          {check.count.toLocaleString()} / {check.total.toLocaleString()}
        </Text>
        {check.count > 0 && (
          <Link to={check.link} data-testid={`dashboard-check-link-${check.key}`}>
            Open
          </Link>
        )}
      </div>
    </div>
    {check.samples.length > 0 && (
      <Collapse ghost>
        <Collapse.Panel
          key={check.key}
          header={`Show ${check.samples.length} example${check.samples.length === 1 ? "" : "s"}`}
        >
          <List
            className="dash-list"
            size="small"
            dataSource={check.samples}
            renderItem={(sample) => (
              <List.Item>
                <div className="dash-sample">
                  <Text code>{sample.label}</Text>
                  {sample.detail && <Text type="secondary">{sample.detail}</Text>}
                </div>
              </List.Item>
            )}
          />
        </Collapse.Panel>
      </Collapse>
    )}
  </List.Item>
);

const providerColumns = [
  {
    title: "Provider",
    dataIndex: "provider_name",
    key: "provider_name",
    render: (name: string, row: ProviderHealth) => (
      <div className="dash-provider-name">
        <Text strong>{name}</Text>
        {!row.is_active && <Tag>Inactive</Tag>}
      </div>
    ),
  },
  {
    title: "Monthly rates",
    key: "monthly_terms",
    render: (_: unknown, row: ProviderHealth) =>
      row.monthly_terms.length === 0 ? (
        <Tag color="red">No terms</Tag>
      ) : (
        <div className="dash-term-tags">
          {row.monthly_terms.map((term) => (
            <Tag key={`${row.provider_id}-${term.months}`}>
              {term.months ?? "?"}mo: {term.interest_value}
            </Tag>
          ))}
        </div>
      ),
  },
  {
    title: "Settings",
    dataIndex: "calculation_settings_count",
    key: "calculation_settings_count",
    width: 90,
  },
  {
    title: "Rates updated",
    key: "terms_last_updated",
    width: 170,
    render: (_: unknown, row: ProviderHealth) => (
      <div className="dash-updated">
        <Text>{formatDate(row.terms_last_updated)}</Text>
        <Text type={row.stale ? "danger" : "secondary"}>
          {formatAge(row.terms_age_days)}
        </Text>
      </div>
    ),
  },
];

export const DashboardIndexComponent = ({
  data,
  loading,
  error,
  refresh,
  firstName,
}: DashboardIndexProps) => {
  if (loading && !data) {
    return (
      <div className="dash-loading" data-testid="dashboard-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return (
      <Alert
        type="error"
        showIcon
        data-testid="dashboard-error"
        message={error ?? "Unable to load dashboard data."}
        action={
          <Button onClick={refresh} data-testid="dashboard-retry">
            Retry
          </Button>
        }
      />
    );
  }

  const issues = data.checks.filter((check) => check.severity !== "ok");
  const passing = data.checks.filter((check) => check.severity === "ok");
  const { critical, warning, ok } = data.summary;

  const healthTitle = (
    <div className="dash-card-title" data-testid="dashboard-health-summary">
      <span>Data health</span>
      {critical > 0 && <Tag color="red">{critical} critical</Tag>}
      {warning > 0 && (
        <Tag color="orange">
          {warning} warning{warning === 1 ? "" : "s"}
        </Tag>
      )}
      {critical === 0 && warning === 0 && <Tag color="green">All checks passing</Tag>}
      {(critical > 0 || warning > 0) && ok > 0 && <Tag>{ok} passing</Tag>}
    </div>
  );

  return (
    <div className="dash-stack">
      <div className="dash-page-head">
        <div>
          <h1 className="dash-page-title">
            {firstName ? `Welcome back, ${firstName}` : "Welcome back"}
          </h1>
          <Text type="secondary">
            Data health and configuration across providers, rates and vehicles.
          </Text>
        </div>
        <div className="dash-page-actions">
          <Text type="secondary">
            Last checked {new Date(data.generated_at).toLocaleString()}
          </Text>
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={refresh}
            data-testid="dashboard-refresh"
          >
            Refresh
          </Button>
        </div>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic title="Vehicles" value={data.totals.vehicles} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Active providers"
              value={data.totals.active_providers}
              suffix={`/ ${data.totals.providers}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic title="TFS SRP year rates" value={data.totals.tfs_srp_year_rates} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="dash-equal-row">
        <Col xs={24} xl={15}>
          <Card title={healthTitle}>
            <div className="dash-scroll" data-testid="dashboard-health-list">
              {issues.length === 0 ? (
                <Empty description="No issues found" />
              ) : (
                <List
                  className="dash-list"
                  dataSource={issues}
                  renderItem={(check) => <CheckItem check={check} />}
                />
              )}
            </div>
            {passing.length > 0 && issues.length > 0 && (
              <Text type="secondary" className="dash-passing">
                Passing: {passing.map((check) => check.title).join(" · ")}
              </Text>
            )}
          </Card>
        </Col>

        <Col xs={24} xl={9}>
          <Card title="Data freshness">
            <div className="dash-fill">
              <List
                className="dash-list"
                dataSource={data.freshness}
                renderItem={(entry: FreshnessEntry) => (
                  <List.Item>
                    <div className="dash-freshness-row">
                      <Text className="dash-freshness-label">{entry.label}</Text>
                      <div className="dash-freshness-age">
                        <Text type={entry.stale ? "danger" : "secondary"}>
                          {formatAge(entry.age_days)}
                        </Text>
                        {entry.stale && <Tag color="orange">Stale</Tag>}
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            </div>
            <Text type="secondary" className="dash-passing">
              Anything not updated in {data.stale_after_days}+ days is flagged as stale.
            </Text>
          </Card>
        </Col>
      </Row>

      <Card title="Providers and rates">
        <Table
          size="small"
          rowKey="provider_id"
          columns={providerColumns}
          dataSource={data.providers}
          pagination={false}
          scroll={{ x: 640, y: 320 }}
        />
      </Card>
    </div>
  );
};
